import React, { useState, useEffect } from 'react';
import { equipmentAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Camera, Plus, Laptop, Mic, Tv, Box } from 'lucide-react';

const TYPE_ICONS = { camera: Camera, laptop: Laptop, microphone: Mic, monitor: Tv, other: Box };

export default function AdminEquipment() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'camera', serial_number: '', status: 'available', condition: 'good', notes: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await equipmentAPI.list();
      setItems(res.data.equipment || []);
    } catch { toast.error('Failed to load equipment'); }
    finally { setLoading(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await equipmentAPI.create(form);
      toast.success('Equipment added');
      setShowCreate(false);
      setForm({ name: '', type: 'camera', serial_number: '', status: 'available', condition: 'good', notes: '' });
      load();
    } catch { toast.error('Failed to add equipment'); }
  };

  const statusColor = (s) => {
    const map = { available: 'bg-green-100 text-green-700', in_use: 'bg-blue-100 text-blue-700', maintenance: 'bg-yellow-100 text-yellow-700', retired: 'bg-gray-100 text-gray-600' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Equipment Management</h1>
          <p className="text-sm text-[#5B616E] mt-1">Track cameras, laptops, microphones and other newsroom equipment</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1E5A8A] hover:bg-[#1E5A8A]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Equipment
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: items.length, color: '#1E5A8A' },
          { label: 'Available', value: items.filter(i => i.status === 'available').length, color: '#1F7A5A' },
          { label: 'In Use', value: items.filter(i => i.status === 'in_use').length, color: '#B89B5E' },
          { label: 'Maintenance', value: items.filter(i => i.status === 'maintenance').length, color: '#B3261E' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
            <p className="text-xs text-[#5B616E] font-medium mb-1">{s.label}</p>
            <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
          <h2 className="text-lg font-semibold mb-4">Add Equipment</h2>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                <option value="camera">Camera</option>
                <option value="laptop">Laptop</option>
                <option value="microphone">Microphone</option>
                <option value="monitor">Monitor</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Serial Number</label>
              <input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                <option value="available">Available</option>
                <option value="in_use">In Use</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Condition</label>
              <select value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div className="md:col-span-3 flex gap-2">
              <Button type="submit" className="bg-[#1E5A8A]">Add Equipment</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Equipment</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Type</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Serial</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Status</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Condition</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DFD5]">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5B616E]">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5B616E]">No equipment registered yet.</td></tr>
              ) : items.map(item => {
                const Icon = TYPE_ICONS[item.type] || Box;
                return (
                  <tr key={item.id} className="hover:bg-[#FBF7EF]">
                    <td className="px-4 py-3 flex items-center gap-3">
                      <Icon className="w-4 h-4 text-[#5B616E]" />
                      <span className="font-medium text-[#1A1A1A]">{item.name}</span>
                    </td>
                    <td className="px-4 py-3 capitalize">{item.type}</td>
                    <td className="px-4 py-3 font-mono text-xs">{item.serial_number || '-'}</td>
                    <td className="px-4 py-3"><Badge className={statusColor(item.status)}>{item.status?.replace('_', ' ')}</Badge></td>
                    <td className="px-4 py-3 capitalize">{item.condition}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
